/**
 * History Command
 * List and manage conversation history
 */

import { Command } from 'commander';
import { getApiClient } from '../api/client.js';
import { isAuthenticated } from '../utils/config.js';
import * as output from '../utils/output.js';

interface ConversationMessage {
    id: number;
    role: 'user' | 'assistant' | 'system';
    content: string;
    createdAt: string;
}

interface Conversation {
    id: number;
    hashKey: string;
    title: string;
    messageCount: number;
    isStarred: boolean;
    isArchived: boolean;
    lastMessageAt?: string;
    lastMessagePreview?: string;
    createdAt: string;
    updatedAt: string;
    messages?: ConversationMessage[];
}

interface HistoryResponse {
    conversations: Conversation[];
    total: number;
    page: number;
    limit: number;
}

function requireAuth(): void {
    if (!isAuthenticated()) {
        output.error('Not logged in. Run `paean login` first.');
        process.exit(1);
    }
}

/**
 * Fetch conversation history from API
 */
async function fetchHistory(options: {
    limit?: number;
    page?: number;
    includeArchived?: boolean;
    starredOnly?: boolean;
}): Promise<HistoryResponse> {
    const client = getApiClient();
    const params = new URLSearchParams();

    if (options.limit) params.set('limit', String(options.limit));
    if (options.page) params.set('page', String(options.page));
    if (options.includeArchived) params.set('includeArchived', 'true');
    if (options.starredOnly) params.set('starred', 'true');
    params.set('clientTypes', 'cli');

    const response = await client.get(`/agent/history?${params.toString()}`);
    return response.data;
}

/**
 * Fetch a single conversation with messages
 */
async function fetchConversation(hashKey: string): Promise<Conversation> {
    const client = getApiClient();
    const response = await client.get(`/agent/history/${hashKey}`);
    return response.data.conversation || response.data;
}

/**
 * Update a conversation (star, rename)
 */
async function updateConversation(hashKey: string, updates: { title?: string; isStarred?: boolean }): Promise<void> {
    const client = getApiClient();
    await client.patch(`/agent/history/${hashKey}`, updates);
}

/**
 * Delete a conversation
 */
async function deleteConversation(hashKey: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`/agent/history/${hashKey}`);
}

/**
 * Archive a conversation
 */
async function archiveConversation(hashKey: string): Promise<void> {
    const client = getApiClient();
    await client.post(`/agent/history/${hashKey}/archive`);
}

/**
 * List conversations command
 */
export const historyCommand = new Command('ls')
    .description('List previous conversation sessions')
    .option('-n, --limit <number>', 'Number of conversations to show', '20')
    .option('-a, --all', 'Include archived conversations')
    .option('-s, --starred', 'Show only starred conversations')
    .option('--json', 'Output as JSON for scripting')
    .action(async (options) => {
        requireAuth();

        try {
            const limit = parseInt(options.limit) || 20;
            const result = await fetchHistory({
                limit,
                includeArchived: options.all,
                starredOnly: options.starred,
            });

            if (options.json) {
                output.json(result);
                return;
            }

            if (result.conversations.length === 0) {
                output.warning('No conversation history found.');
                output.dim('Start a new conversation with: paean');
                return;
            }

            output.header('Recent Conversations');

            result.conversations.forEach((conv, index) => {
                const num = output.colors.dim(`${index + 1}.`);
                const id = output.colors.info(conv.hashKey.substring(0, 8));
                const star = conv.isStarred ? output.colors.warning('* ') : '';
                const archived = conv.isArchived ? output.colors.dim('[archived] ') : '';
                const title = output.truncate(conv.title || 'Untitled', 40);
                const msgs = output.colors.dim(`${conv.messageCount} msgs`);
                const time = output.colors.dim(output.formatRelativeTime(conv.lastMessageAt || conv.updatedAt));

                console.log(`${num} ${id}  ${star}${archived}${title}  ${msgs}  ${time}`);
            });

            output.newline();
            output.dim(`Showing ${result.conversations.length} of ${result.total} conversations`);
            output.dim('Resume: paean resume <id> | Show: paean show <id> | Star: paean star <id>');
            output.newline();

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            output.errorWithHint(`Failed to fetch history: ${errorMessage}`);
            process.exit(1);
        }
    });

/**
 * Show a specific conversation with its messages
 */
export const showCommand = new Command('show')
    .description('Show a conversation with its messages')
    .argument('<id>', 'Conversation ID (full or partial hash)')
    .option('--json', 'Output as JSON')
    .action(async (id: string, options) => {
        requireAuth();

        try {
            const conv = await fetchConversation(id);

            if (options.json) {
                output.json(conv);
                return;
            }

            const star = conv.isStarred ? output.colors.warning(' *') : '';
            const archived = conv.isArchived ? output.colors.dim(' [archived]') : '';

            output.header(`${conv.title || 'Untitled'}${star}${archived}`);
            output.dim(`ID: ${conv.hashKey} | ${conv.messageCount} messages | ${output.formatRelativeTime(conv.updatedAt)}`);

            if (conv.messages && conv.messages.length > 0) {
                for (const msg of conv.messages) {
                    const roleLabel = msg.role === 'user'
                        ? output.colors.info('You')
                        : msg.role === 'assistant'
                            ? output.colors.primary('Paean')
                            : output.colors.dim('System');
                    const time = output.colors.dim(output.formatRelativeTime(msg.createdAt));
                    console.log(`\n${roleLabel} ${time}`);
                    console.log(msg.content);
                }
            } else {
                output.dim('\nNo messages to display.');
            }

            output.newline();
            output.dim(`Resume: paean resume ${conv.hashKey.substring(0, 8)}`);
            output.newline();
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            output.errorWithHint(`Failed to show conversation: ${errorMessage}`);
            process.exit(1);
        }
    });

/**
 * Star/unstar a conversation
 */
export const starCommand = new Command('star')
    .description('Toggle star on a conversation')
    .argument('<id>', 'Conversation ID')
    .action(async (id: string) => {
        requireAuth();

        try {
            const conv = await fetchConversation(id);
            const newStarred = !conv.isStarred;
            await updateConversation(id, { isStarred: newStarred });
            if (newStarred) {
                output.success(`Starred: ${conv.title || 'Untitled'}`);
            } else {
                output.dim(`Unstarred: ${conv.title || 'Untitled'}`);
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            output.errorWithHint(`Failed to update conversation: ${errorMessage}`);
            process.exit(1);
        }
    });

/**
 * Rename a conversation
 */
export const renameCommand = new Command('rename')
    .description('Rename a conversation')
    .argument('<id>', 'Conversation ID')
    .argument('<title>', 'New title')
    .action(async (id: string, title: string) => {
        requireAuth();

        try {
            await updateConversation(id, { title });
            output.success(`Renamed to: ${title}`);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            output.errorWithHint(`Failed to rename conversation: ${errorMessage}`);
            process.exit(1);
        }
    });

/**
 * Delete a conversation
 */
export const deleteCommand = new Command('delete')
    .description('Delete a conversation')
    .argument('<id>', 'Conversation ID')
    .action(async (id: string) => {
        requireAuth();

        try {
            await deleteConversation(id);
            output.success('Conversation deleted.');
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            output.errorWithHint(`Failed to delete conversation: ${errorMessage}`);
            process.exit(1);
        }
    });

/**
 * Archive a conversation
 */
export const archiveCommand = new Command('archive')
    .description('Archive a conversation')
    .argument('<id>', 'Conversation ID')
    .action(async (id: string) => {
        requireAuth();

        try {
            await archiveConversation(id);
            output.success('Conversation archived.');
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            output.errorWithHint(`Failed to archive conversation: ${errorMessage}`);
            process.exit(1);
        }
    });

/**
 * Resume conversation command
 */
export const resumeCommand = new Command('resume')
    .description('Resume a previous conversation session')
    .argument('[id]', 'Conversation ID to resume (defaults to latest)')
    .action(async (id?: string) => {
        requireAuth();

        try {
            let conversationId = id;

            if (!conversationId) {
                const result = await fetchHistory({ limit: 1 });

                if (result.conversations.length === 0) {
                    output.warning('No conversation history found.');
                    output.dim('Start a new conversation with: paean');
                    return;
                }

                conversationId = result.conversations[0].hashKey;
                output.dim(`Resuming latest: "${result.conversations[0].title || 'Untitled'}"`);
            }

            const { runAgentMode } = await import('./agent.js');
            await runAgentMode({
                mcp: true,
                resumeConversationId: conversationId,
            });

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            output.errorWithHint(`Failed to resume: ${errorMessage}`);
            process.exit(1);
        }
    });
